import { useState, useEffect } from "react"
import { Label } from "reactstrap"
import CreatableSelect from "react-select/creatable"
import { components as selectComponents } from "react-select"
import { useVehicleOptions } from "hooks/stockHook"
import {
  buildVehicleOptionLabel,
  buildVehicleHeading,
  buildVehicleSubheading,
} from "helpers/stock_helpers"

// Dropdown row: heading/subheading text colors pinned to the row's own
// hover/selected state, instead of formatOptionLabel's static Bootstrap
// classes (fw-semibold / text-muted) which don't track react-select's state
// and go unreadable against the hover/selected background.
const VehicleOption = props => {
  const { data, isFocused, isSelected } = props
  const heading = data.heading || data.label

  const isHighlighted = isFocused || isSelected
  const textColor = isHighlighted ? "#fff" : "#212529"
  const mutedColor = isHighlighted ? "rgba(255,255,255,0.7)" : "#6c757d"

  return (
    <selectComponents.Option {...props}>
      <div style={{ color: textColor }}>
        <div style={{ fontWeight: 600 }}>{heading}</div>
        {data.subheading && (
          <div style={{ fontSize: "0.8125rem", color: mutedColor }}>
            {data.subheading}
          </div>
        )}
      </div>
    </selectComponents.Option>
  )
}

const VehicleIdSelect = ({ formik, onVehicleSelect, actions, isLabel=true, label="Vehicle ID" }) => {
  const [inputValue, setInputValue] = useState("")
  const [debouncedValue, setDebouncedValue] = useState("")
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  const formikValue = formik?.values?.vehicle_id

  // 🔁 Debounce typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue.trim())
    }, 500)
    return () => clearTimeout(handler)
  }, [inputValue])

  const { data, isFetching } = useVehicleOptions(
    { identifier: debouncedValue || (formikValue ? String(formikValue) : '') },
    true
  )

  // 🔄 Map API data to options
  useEffect(() => {
    if (data?.data?.length) {
      setOptions(
        data.data.map(item => ({
          label: buildVehicleOptionLabel(item),
          value: item.id,
          raw: item,
          heading: buildVehicleHeading(item),
          subheading: buildVehicleSubheading(item),
        }))
      )
    } else {
      setOptions([])
    }
  }, [data])

  // 🧠 Handle select
  const handleChange = option => {
    setSelectedOption(option)
    formik.setFieldValue("vehicle_id", option ? option.value : null)
    onVehicleSelect?.(option?.raw ?? null)
  }

  // ➕ Handle create
  const handleCreate = input => {
    const created = {
      label: input,
      value: input,
      __isNew__: true,
    }

    setOptions(prev => [...prev, created])
    setSelectedOption(created)
    formik.setFieldValue("vehicle_id", input)
  }

  // 🔁 Sync formik value (EDIT SAFE)
  useEffect(() => {
    const value = formikValue

    if (value === null || value === undefined || value === "") {
      setSelectedOption(null)
      return
    }

    // If value is object (when parent sets {label,value})
    if (typeof value === "object" && "value" in value) {
      setSelectedOption(value)
      return
    }

    // Try to find matching option from loaded options
    const found = options.find(opt => String(opt.value) === String(value))
    if (found) {
      setSelectedOption(found)
      return
    }

    // Fallback: show raw value as label
    setSelectedOption({ label: String(value), value })
  }, [formikValue, options])

  // 🎨 Styles (unchanged)
  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#fff",
      borderColor: state.isFocused ? "#80bdff" : "#d1d5db",
      boxShadow: state.isFocused
        ? "0 0 0 0.15rem rgba(0,123,255,0.25)"
        : "none",
    }),
    menu: base => ({
      ...base,
      zIndex: 10,
    }),
  }

  return (
    <>
      {isLabel?<div className="d-flex justify-content-between align-items-center mb-1">
        <Label className="mb-0">{label}</Label>
        {actions}
      </div>:""}

      <CreatableSelect
        value={selectedOption}
        options={options}
        isClearable
        isSearchable
        isLoading={isFetching}
        onChange={handleChange}
        onCreateOption={handleCreate}
        onInputChange={setInputValue}
        filterOption={() => true}
        placeholder={
          isFetching
            ? "Loading..."
            : "Stock ID, chassis, model, mileage (e.g. 30000km)..."
        }
        noOptionsMessage={() =>
          debouncedValue
            ? "No vehicle found — press Enter to create"
            : "No recent vehicles found"
        }
        formatOptionLabel={option => <span>{option.heading || option.label}</span>}
        components={{ Option: VehicleOption }}
        styles={customStyles}
      />

      {!selectedOption && !debouncedValue && (
        <div className="form-text text-muted">
          Try: axio black 2021 · hiace hybrid · 30000km
        </div>
      )}

      {formik?.touched?.vehicle_id && formik?.errors?.vehicle_id && (
        <div className="invalid-feedback d-block">
          {formik.errors.vehicle_id}
        </div>
      )}
    </>
  )
}

export default VehicleIdSelect
